import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { PrestaShopService } from '../services/prestashop';
import { ShopifyService } from '../services/shopify';
import { Json } from '../types/supabase';
import { isDevelopmentMode } from '../services/mockApi';

// Récupérer les variables d'environnement Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Import the config interfaces from the service files
interface ShopifyConfig {
  shopDomain: string;
  accessToken: string;
}

interface PrestaShopConfig {
  shopUrl: string;
  apiKey: string;
}

interface IntegrationConfig {
  shopify?: {
    enabled: boolean;
    config: ShopifyConfig;
  };
  prestashop?: {
    enabled: boolean;
    config: PrestaShopConfig;
  };
}

interface ProductIntegrationsStore {
  config: IntegrationConfig;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  shopifyService: ShopifyService | null;
  prestashopService: PrestaShopService | null;
  initializeServices: (chatbotPublicId: string) => Promise<void>;
  updateShopifyConfig: (config: Partial<IntegrationConfig['shopify']>, chatbotPublicId: string) => Promise<void>;
  updatePrestaShopConfig: (config: Partial<IntegrationConfig['prestashop']>, chatbotPublicId: string) => Promise<void>;
  syncProducts: (source: 'shopify' | 'prestashop') => Promise<void>;
  registerWebhook: (source: 'shopify' | 'prestashop', chatbotPublicId: string) => Promise<void>;
}

const DEFAULT_CONFIG: IntegrationConfig = {
  shopify: {
    enabled: false,
    config: {
      shopDomain: '',
      accessToken: ''
    }
  },
  prestashop: {
    enabled: false,
    config: {
      shopUrl: '',
      apiKey: ''
    }
  }
};

export const useProductIntegrations = create<ProductIntegrationsStore>((set, get) => ({
  config: DEFAULT_CONFIG,
  loading: false,
  error: null,
  initialized: false,
  shopifyService: null,
  prestashopService: null,

  initializeServices: async (chatbotPublicId) => {
    try {
      set({ loading: true, error: null });

      // Récupérer la configuration
      const { data: config, error } = await supabase
        .from('product_integrations')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      if (config) {
        const shopifyConfig = config.find(c => c.type === 'shopify');
        const prestashopConfig = config.find(c => c.type === 'prestashop');

        // Initialiser les services si les configurations sont valides
        let shopifyService = null;
        let prestashopService = null;

        const shopifyConfigData = shopifyConfig?.config as ShopifyConfig | undefined;
        const prestashopConfigData = prestashopConfig?.config as PrestaShopConfig | undefined;

        if (shopifyConfig?.is_active && shopifyConfigData) {
          shopifyService = new ShopifyService(shopifyConfigData, chatbotPublicId);
        }

        if (prestashopConfig?.is_active && prestashopConfigData) {
          prestashopService = new PrestaShopService(prestashopConfigData, chatbotPublicId);
        }

        set({
          config: {
            shopify: {
              enabled: shopifyConfig?.is_active || false,
              config: shopifyConfigData || DEFAULT_CONFIG.shopify!.config
            },
            prestashop: {
              enabled: prestashopConfig?.is_active || false,
              config: prestashopConfigData || DEFAULT_CONFIG.prestashop!.config
            }
          },
          shopifyService,
          prestashopService,
          initialized: true
        });
      }
    } catch (err) {
      console.error('Error initializing product integrations:', err);
      set({ error: err instanceof Error ? err.message : 'Error initializing integrations' });
    } finally {
      set({ loading: false });
    }
  },

  updateShopifyConfig: async (newConfig, chatbotPublicId) => {
    try {
      set({ loading: true, error: null });

      if (!newConfig) {
        throw new Error('Config is required');
      }

      // Vérifier d'abord si un enregistrement existe
      const { data: existingConfig, error: fetchError } = await supabase
        .from('product_integrations')
        .select('*')
        .eq('type', 'shopify')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      let error;
      
      if (fetchError && fetchError.code !== 'PGRST116') {
        // PGRST116 signifie "aucun résultat trouvé", ce qui est OK
        throw fetchError;
      }

      if (existingConfig) {
        // Si l'enregistrement existe, le mettre à jour
        const { error: updateError } = await supabase
          .from('product_integrations')
          .update({
            config: newConfig.config as unknown as Json,
            is_active: newConfig.enabled !== undefined ? newConfig.enabled : false
          })
          .eq('type', 'shopify')
          .eq('chatbot_public_id', chatbotPublicId);
          
        error = updateError;
      } else {
        // Si l'enregistrement n'existe pas, l'insérer
        const { error: insertError } = await supabase
          .from('product_integrations')
          .insert({
            chatbot_public_id: chatbotPublicId,
            type: 'shopify',
            config: newConfig.config as unknown as Json,
            is_active: newConfig.enabled !== undefined ? newConfig.enabled : false
          });
          
        error = insertError;
      }

      if (error) throw error;

      // Mettre à jour le service
      let shopifyService = null;
      if (newConfig.enabled && newConfig.config) {
        shopifyService = new ShopifyService(newConfig.config, chatbotPublicId);
        
        // Register webhook if enabled
        if (newConfig.enabled) {
          await get().registerWebhook('shopify', chatbotPublicId);
        }
      }

      set((state) => ({
        ...state,
        config: {
          ...state.config,
          shopify: {
            enabled: newConfig.enabled !== undefined ? newConfig.enabled : state.config.shopify?.enabled || false,
            config: newConfig.config || state.config.shopify?.config || DEFAULT_CONFIG.shopify!.config
          }
        },
        shopifyService
      }));
    } catch (err) {
      console.error('Error updating Shopify config:', err);
      set({ error: err instanceof Error ? err.message : 'Error updating Shopify config' });
    } finally {
      set({ loading: false });
    }
  },

  updatePrestaShopConfig: async (newConfig, chatbotPublicId) => {
    try {
      set({ loading: true, error: null });

      if (!newConfig) {
        throw new Error('Config is required');
      }

      // Vérifier d'abord si un enregistrement existe
      const { data: existingConfig, error: fetchError } = await supabase
        .from('product_integrations')
        .select('*')
        .eq('type', 'prestashop')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      let error;
      
      if (fetchError && fetchError.code !== 'PGRST116') {
        // PGRST116 signifie "aucun résultat trouvé", ce qui est OK
        throw fetchError;
      }

      if (existingConfig) {
        // Si l'enregistrement existe, le mettre à jour
        const { error: updateError } = await supabase
          .from('product_integrations')
          .update({
            config: newConfig.config as unknown as Json,
            is_active: newConfig.enabled !== undefined ? newConfig.enabled : false
          })
          .eq('type', 'prestashop')
          .eq('chatbot_public_id', chatbotPublicId);
          
        error = updateError;
      } else {
        // Si l'enregistrement n'existe pas, l'insérer
        const { error: insertError } = await supabase
          .from('product_integrations')
          .insert({
            chatbot_public_id: chatbotPublicId,
            type: 'prestashop',
            config: newConfig.config as unknown as Json,
            is_active: newConfig.enabled !== undefined ? newConfig.enabled : false
          });
          
        error = insertError;
      }

      if (error) throw error;

      // Mettre à jour le service
      let prestashopService = null;
      if (newConfig.enabled && newConfig.config) {
        prestashopService = new PrestaShopService(newConfig.config, chatbotPublicId);
        
        // Register webhook if enabled
        if (newConfig.enabled) {
          await get().registerWebhook('prestashop', chatbotPublicId);
        }
      }

      set((state) => ({
        ...state,
        config: {
          ...state.config,
          prestashop: {
            enabled: newConfig.enabled !== undefined ? newConfig.enabled : state.config.prestashop?.enabled || false,
            config: newConfig.config || state.config.prestashop?.config || DEFAULT_CONFIG.prestashop!.config
          }
        },
        prestashopService
      }));
    } catch (err) {
      console.error('Error updating PrestaShop config:', err);
      set({ error: err instanceof Error ? err.message : 'Error updating PrestaShop config' });
    } finally {
      set({ loading: false });
    }
  },

  syncProducts: async (source) => {
    try {
      set({ loading: true, error: null });

      if (source === 'shopify') {
        const service = get().shopifyService;
        if (!service) throw new Error('Shopify service not initialized');
        await service.syncProducts();
      } else {
        const service = get().prestashopService;
        if (!service) throw new Error('PrestaShop service not initialized');
        await service.syncProducts();
      }
    } catch (err) {
      console.error('Error syncing products:', err);
      set({ error: err instanceof Error ? err.message : 'Error syncing products' });
    } finally {
      set({ loading: false });
    }
  },
  
  registerWebhook: async (source: 'shopify' | 'prestashop', chatbotPublicId: string) => {
    try {
      set({ loading: true, error: null });
      
      // Extraire le project ID de l'URL Supabase
      const projectId = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'ddounabnvgpiszavsudj';
      
      // Construire l'URL de la fonction Edge pour les webhooks
      const webhookUrl = `https://${projectId}.supabase.co/functions/v1/product-webhook?provider=${source}&chatbot_id=${chatbotPublicId}`;
      
      console.log(`Registering ${source} webhooks for ${chatbotPublicId} at ${webhookUrl}`);

      if (source === 'shopify') {
        const shopifyService = get().shopifyService;
        if (shopifyService) {
          // Enregistrer les webhooks pour différents événements Shopify
          await shopifyService.registerWebhook('products/create', webhookUrl);
          await shopifyService.registerWebhook('products/update', webhookUrl);
          await shopifyService.registerWebhook('products/delete', webhookUrl);
          await shopifyService.registerWebhook('orders/create', webhookUrl);
          await shopifyService.registerWebhook('orders/updated', webhookUrl);
          await shopifyService.registerWebhook('inventory_levels/update', webhookUrl);
        }
      } else if (source === 'prestashop') {
        // TODO: Implémenter l'enregistrement des webhooks PrestaShop
        // Ceci nécessite généralement un module personnalisé dans PrestaShop
      }
    } catch (err) {
      console.error('Error registering webhook:', err);
      set({ error: err instanceof Error ? err.message : 'Error registering webhook' });
    } finally {
      set({ loading: false });
    }
  }
}));